import {useCallback, useEffect, useRef, useState} from 'react'
import {
  Animated,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import * as Haptics from 'expo-haptics'

import {type ThemePlayerProps} from '#/types/capsule'
import {type ClassicQuizSettings} from './settingsSchema'

type AnswerState = 'idle' | 'submitted'

function getSettings(raw: Record<string, unknown>): ClassicQuizSettings {
  return {
    instantFeedback: raw['instantFeedback'] !== false,
    shuffleQuestions: raw['shuffleQuestions'] === true,
    showProgressBar: raw['showProgressBar'] !== false,
    primaryColor:
      typeof raw['primaryColor'] === 'string' ? raw['primaryColor'] : '#0085ff',
  }
}

export function PlayerComponent({
  capsule,
  currentPageIndex,
  onPageComplete,
}: ThemePlayerProps) {
  const settings = getSettings(capsule.behaviorSettings)
  const page = capsule.slides[currentPageIndex]
  // multi-select: stores selected labels / values
  const [selected, setSelected] = useState<string[]>([])
  const [answerState, setAnswerState] = useState<AnswerState>('idle')
  // fill_blank: map blankId → userAnswer
  const [blankAnswers, setBlankAnswers] = useState<Record<string, string>>({})
  // matching: map pairId → right value chosen by user
  const [matchAnswers, setMatchAnswers] = useState<Record<string, string>>({})
  const startTimeRef = useRef(Date.now())
  const shakeAnim = useRef(new Animated.Value(0)).current
  const scaleAnims = useRef<Animated.Value[]>([]).current

  const options =
    page?.type === 'true_false'
      ? [
          {
            id: 'true',
            label: 'True',
            isCorrect: page.answer === true || page.answer === 'true',
          },
          {
            id: 'false',
            label: 'False',
            isCorrect: page.answer === false || page.answer === 'false',
          },
        ]
      : (page?.options ?? []).map(o => ({...o, id: o.id ?? o.label}))

  while (scaleAnims.length < options.length) {
    scaleAnims.push(new Animated.Value(1))
  }

  useEffect(() => {
    setSelected([])
    setAnswerState('idle')
    setBlankAnswers({})
    setMatchAnswers({})
    startTimeRef.current = Date.now()
    scaleAnims.forEach(a => a.setValue(1))
  }, [currentPageIndex])

  const shake = useCallback(() => {
    shakeAnim.setValue(0)
    Animated.sequence([
      Animated.timing(shakeAnim, {
        toValue: 8,
        duration: 55,
        useNativeDriver: true,
      }),
      Animated.timing(shakeAnim, {
        toValue: -8,
        duration: 55,
        useNativeDriver: true,
      }),
      Animated.timing(shakeAnim, {
        toValue: 4,
        duration: 55,
        useNativeDriver: true,
      }),
      Animated.timing(shakeAnim, {
        toValue: 0,
        duration: 55,
        useNativeDriver: true,
      }),
    ]).start()
  }, [shakeAnim])

  const scaleUp = useCallback(
    (idx: number) => {
      Animated.spring(scaleAnims[idx], {
        toValue: 1.04,
        useNativeDriver: true,
        tension: 220,
        friction: 8,
      }).start()
    },
    [scaleAnims],
  )

  // ── Single-tap selection for choice types ─────────────────────────────────
  const handleToggle = useCallback(
    (label: string, idx: number) => {
      if (answerState !== 'idle' || !page) return
      if (page.type === 'multiple_choice') {
        setSelected(prev =>
          prev.includes(label)
            ? prev.filter(x => x !== label)
            : [...prev, label],
        )
        return
      }
      // single_choice / true_false / image_choice — instant submit
      setSelected([label])
      const opt = options.find(o => o.label === label)
      const isCorrect = !!opt?.isCorrect
      setAnswerState('submitted')
      if (isCorrect) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
        scaleUp(idx)
      } else {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error)
        shake()
      }
      if (settings.instantFeedback) {
        setTimeout(() => {
          onPageComplete({
            pageId: page.id,
            isCorrect,
            timeSpentMs: Date.now() - startTimeRef.current,
            userAnswer: label,
          })
        }, 900)
      }
    },
    [answerState, page, options, settings, shake, scaleUp, onPageComplete],
  )

  // ── Submit for types that need an explicit confirm ────────────────────────
  const handleSubmit = useCallback(() => {
    if (!page || answerState !== 'idle') return
    setAnswerState('submitted')

    let isCorrect = false
    let userAnswer: unknown = selected

    if (page.type === 'multiple_choice') {
      const correctLabels = new Set(
        (page.options ?? []).filter(o => o.isCorrect).map(o => o.label),
      )
      const selectedSet = new Set(selected)
      isCorrect =
        correctLabels.size === selectedSet.size &&
        [...correctLabels].every(x => selectedSet.has(x))
    } else if (page.type === 'fill_blank') {
      const blanks = page.blanks ?? []
      isCorrect = blanks.every(b => {
        const ua = (blankAnswers[b.id] ?? '').trim().toLowerCase()
        if (ua === b.answer.trim().toLowerCase()) return true
        return (b.alternatives ?? []).some(
          alt => alt.trim().toLowerCase() === ua,
        )
      })
      userAnswer = blankAnswers
    } else if (page.type === 'matching') {
      const pairs = page.pairs ?? []
      isCorrect = pairs.every(
        p => matchAnswers[p.id]?.trim() === p.right.trim(),
      )
      userAnswer = matchAnswers
    } else if (page.type === 'ordering') {
      // ordering: selected holds item ids in user-chosen order
      const correct = page.correctOrder ?? []
      isCorrect =
        selected.length === correct.length &&
        selected.every((id, i) => id === correct[i])
      userAnswer = selected
    }

    if (isCorrect) {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
    } else {
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error)
      shake()
    }

    if (settings.instantFeedback) {
      setTimeout(() => {
        onPageComplete({
          pageId: page.id,
          isCorrect,
          timeSpentMs: Date.now() - startTimeRef.current,
          userAnswer,
        })
      }, 900)
    }
  }, [
    page,
    answerState,
    selected,
    blankAnswers,
    matchAnswers,
    settings,
    shake,
    onPageComplete,
  ])

  if (!page) return null

  const primary = settings.primaryColor
  const totalPages = capsule.slides.length
  const needsSubmit =
    page.type === 'multiple_choice' ||
    page.type === 'fill_blank' ||
    page.type === 'matching' ||
    page.type === 'ordering'

  // ── Content slide ─────────────────────────────────────────────────────────
  if (page.type === 'content') {
    return (
      <ScrollView
        style={styles.root}
        contentContainerStyle={styles.scrollContent}>
        {page.contentTitle ? (
          <Text style={styles.contentTitle}>{page.contentTitle}</Text>
        ) : null}
        {page.contentText ? (
          <Text style={styles.contentBody}>{page.contentText}</Text>
        ) : null}
        <TouchableOpacity
          accessibilityRole="button"
          style={[styles.submitBtn, {backgroundColor: primary}]}
          onPress={() =>
            onPageComplete({
              pageId: page.id,
              isCorrect: true,
              timeSpentMs: Date.now() - startTimeRef.current,
              userAnswer: null,
            })
          }>
          <Text style={styles.submitTxt}>Continue →</Text>
        </TouchableOpacity>
      </ScrollView>
    )
  }

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.scrollContent}>
      {settings.showProgressBar && (
        <View style={styles.progressOuter}>
          {Array.from({length: totalPages}).map((_, i) => (
            <View
              key={i}
              style={[
                styles.progressSegment,
                {
                  flex: 1,
                  backgroundColor:
                    i < currentPageIndex
                      ? primary
                      : i === currentPageIndex
                        ? primary + '88'
                        : 'rgba(255,255,255,0.12)',
                },
              ]}
            />
          ))}
        </View>
      )}

      <Animated.View
        style={[styles.questionCard, {transform: [{translateX: shakeAnim}]}]}>
        <Text style={styles.questionCounter}>
          {currentPageIndex + 1} / {totalPages}
        </Text>
        <Text style={styles.questionText}>{page.question}</Text>
      </Animated.View>

      {/* ── Choice options (single / multiple / true_false / image_choice) ── */}
      {(page.type === 'single_choice' ||
        page.type === 'multiple_choice' ||
        page.type === 'true_false' ||
        page.type === 'image_choice') && (
        <View style={styles.options}>
          {options.map((opt, idx) => {
            const isSel = selected.includes(opt.label)
            const isCorrect = opt.isCorrect
            let bgColor = 'rgba(255,255,255,0.05)'
            let borderColor = 'rgba(255,255,255,0.12)'
            if (answerState === 'submitted') {
              if (isSel && isCorrect) {
                bgColor = 'rgba(34,197,94,0.2)'
                borderColor = '#22c55e'
              } else if (isSel && !isCorrect) {
                bgColor = 'rgba(239,68,68,0.2)'
                borderColor = '#ef4444'
              } else if (!isSel && isCorrect) {
                bgColor = 'rgba(34,197,94,0.12)'
                borderColor = '#22c55e'
              }
            } else if (isSel) {
              bgColor = primary + '22'
              borderColor = primary
            }
            return (
              <Animated.View
                key={opt.id}
                style={[
                  {
                    transform: [
                      {scale: scaleAnims[idx] ?? new Animated.Value(1)},
                    ],
                  },
                ]}>
                <Pressable
                  accessibilityRole="button"
                  onPress={() => handleToggle(opt.label, idx)}
                  disabled={answerState !== 'idle'}
                  style={[
                    styles.optionBtn,
                    {backgroundColor: bgColor, borderColor},
                  ]}>
                  <View
                    style={[
                      styles.optionBadge,
                      {backgroundColor: primary + '22'},
                    ]}>
                    <Text style={[styles.optionBadgeText, {color: primary}]}>
                      {String.fromCharCode(65 + idx)}
                    </Text>
                  </View>
                  <Text style={styles.optionText}>{opt.label}</Text>
                  {answerState === 'submitted' && isCorrect && (
                    <Text style={styles.correctMark}>✓</Text>
                  )}
                </Pressable>
              </Animated.View>
            )
          })}
        </View>
      )}

      {/* ── Fill in the blank ─────────────────────────────────────────────── */}
      {page.type === 'fill_blank' && (
        <View style={styles.options}>
          {(page.blanks ?? []).map((blank, idx) => {
            const ua = blankAnswers[blank.id] ?? ''
            const isCorrect =
              answerState === 'submitted' &&
              (ua.trim().toLowerCase() === blank.answer.trim().toLowerCase() ||
                (blank.alternatives ?? []).some(
                  a => a.trim().toLowerCase() === ua.trim().toLowerCase(),
                ))
            const isWrong = answerState === 'submitted' && !isCorrect
            return (
              <View key={blank.id} style={styles.blankRow}>
                {blank.before ? (
                  <Text style={styles.blankContext}>{blank.before}</Text>
                ) : null}
                <TextInput
                  accessibilityLabel="Text input field"
                  accessibilityHint="Enter your answer for this blank"
                  style={[
                    styles.blankInput,
                    isCorrect && {borderColor: '#22c55e'},
                    isWrong && {borderColor: '#ef4444'},
                  ]}
                  value={ua}
                  onChangeText={v =>
                    setBlankAnswers(prev => ({...prev, [blank.id]: v}))
                  }
                  placeholder={`Blank ${idx + 1}`}
                  placeholderTextColor="rgba(255,255,255,0.3)"
                  editable={answerState === 'idle'}
                />
                {blank.after ? (
                  <Text style={styles.blankContext}>{blank.after}</Text>
                ) : null}
                {answerState === 'submitted' && isWrong && (
                  <Text style={styles.blankCorrect}>→ {blank.answer}</Text>
                )}
              </View>
            )
          })}
        </View>
      )}

      {/* ── Matching pairs ────────────────────────────────────────────────── */}
      {page.type === 'matching' && (
        <View style={styles.options}>
          {(page.pairs ?? []).map(pair => {
            const ua = matchAnswers[pair.id] ?? ''
            const isCorrect =
              answerState === 'submitted' && ua.trim() === pair.right.trim()
            const isWrong = answerState === 'submitted' && !isCorrect
            return (
              <View key={pair.id} style={styles.matchRow}>
                <View style={styles.matchLeft}>
                  <Text style={styles.matchLeftTxt}>{pair.left}</Text>
                </View>
                <Text style={styles.pairArrow}>↔</Text>
                <TextInput
                  accessibilityLabel="Text input field"
                  accessibilityHint="Enter the matching value"
                  style={[
                    styles.matchInput,
                    {borderColor: primary + '44'},
                    isCorrect && {borderColor: '#22c55e'},
                    isWrong && {borderColor: '#ef4444'},
                  ]}
                  value={ua}
                  onChangeText={v =>
                    setMatchAnswers(prev => ({...prev, [pair.id]: v}))
                  }
                  placeholder="Match…"
                  placeholderTextColor="rgba(255,255,255,0.3)"
                  editable={answerState === 'idle'}
                />
                {answerState === 'submitted' && isWrong && (
                  <Text style={styles.blankCorrect}>→ {pair.right}</Text>
                )}
              </View>
            )
          })}
        </View>
      )}

      {/* ── Ordering ──────────────────────────────────────────────────────── */}
      {page.type === 'ordering' && (
        <View style={styles.options}>
          <Text style={styles.orderHint}>Tap items in the correct order:</Text>
          {(page.orderItems ?? []).map(item => {
            const pos = selected.indexOf(item.id)
            const isSelected = pos !== -1
            const correctIdx = (page.correctOrder ?? []).indexOf(item.id)
            const isRight =
              answerState === 'submitted' && isSelected && pos === correctIdx
            const isWrong =
              answerState === 'submitted' && isSelected && pos !== correctIdx
            return (
              <Pressable
                accessibilityRole="button"
                key={item.id}
                disabled={answerState !== 'idle'}
                onPress={() => {
                  if (isSelected) return
                  setSelected(prev => [...prev, item.id])
                }}
                style={[
                  styles.orderBtn,
                  isSelected && {
                    borderColor: primary,
                    backgroundColor: primary + '22',
                  },
                  isRight && {
                    borderColor: '#22c55e',
                    backgroundColor: 'rgba(34,197,94,0.15)',
                  },
                  isWrong && {
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239,68,68,0.15)',
                  },
                ]}>
                {isSelected && (
                  <View style={[styles.orderBadge, {backgroundColor: primary}]}>
                    <Text style={styles.orderBadgeTxt}>{pos + 1}</Text>
                  </View>
                )}
                <Text style={styles.optionText}>{item.label}</Text>
              </Pressable>
            )
          })}
          {selected.length > 0 && answerState === 'idle' && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => setSelected([])}
              style={styles.resetBtn}>
              <Text style={[styles.resetTxt, {color: primary}]}>
                Reset order
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* ── Explanation ──────────────────────────────────────────────────── */}
      {page.explanation && answerState === 'submitted' && (
        <View style={[styles.explanation, {borderLeftColor: primary}]}>
          <Text style={[styles.explanationLabel, {color: primary}]}>
            Explanation
          </Text>
          <Text style={styles.explanationText}>{page.explanation}</Text>
        </View>
      )}

      {/* ── Submit button (multi-step types) ─────────────────────────────── */}
      {needsSubmit && answerState === 'idle' && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={handleSubmit}
          style={[styles.submitBtn, {backgroundColor: primary}]}>
          <Text style={styles.submitTxt}>Submit</Text>
        </TouchableOpacity>
      )}
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  root: {flex: 1, backgroundColor: '#0a1220'},
  scrollContent: {
    paddingHorizontal: 20,
    paddingBottom: 32,
    paddingTop: 16,
    gap: 0,
  },
  progressOuter: {
    flexDirection: 'row',
    gap: 3,
    height: 3,
    borderRadius: 2,
    marginBottom: 24,
    overflow: 'hidden',
  },
  progressSegment: {height: 3, borderRadius: 2},
  questionCard: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    padding: 22,
    marginBottom: 16,
  },
  questionCounter: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.35)',
    marginBottom: 10,
    letterSpacing: 1,
    fontWeight: '600',
  },
  questionText: {
    fontSize: 20,
    fontWeight: '700',
    color: '#e8f1f8',
    lineHeight: 28,
  },
  options: {gap: 10, marginBottom: 16},
  optionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1.5,
    padding: 13,
    gap: 12,
  },
  optionBadge: {
    width: 30,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionBadgeText: {fontSize: 13, fontWeight: '700'},
  optionText: {flex: 1, fontSize: 15, color: '#e8f1f8', fontWeight: '500'},
  correctMark: {fontSize: 17, color: '#22c55e', fontWeight: '700'},
  explanation: {
    marginTop: 8,
    marginBottom: 16,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: 10,
    padding: 13,
    borderLeftWidth: 3,
  },
  explanationLabel: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 4,
  },
  explanationText: {
    fontSize: 13,
    color: 'rgba(232,241,248,0.7)',
    lineHeight: 19,
  },
  submitBtn: {
    marginTop: 8,
    paddingVertical: 14,
    borderRadius: 30,
    alignItems: 'center',
  },
  submitTxt: {color: '#fff', fontSize: 15, fontWeight: '700'},
  blankRow: {gap: 6, marginBottom: 4},
  blankContext: {color: 'rgba(232,241,248,0.6)', fontSize: 13},
  blankInput: {
    color: '#fff',
    fontSize: 14,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  blankCorrect: {color: '#22c55e', fontSize: 12, fontWeight: '600'},
  matchRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
  matchLeft: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  matchLeftTxt: {color: '#e8f1f8', fontSize: 13, fontWeight: '600'},
  pairArrow: {color: 'rgba(255,255,255,0.4)', fontSize: 14},
  matchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 13,
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: 10,
    borderWidth: 1.5,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  orderHint: {color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 8},
  orderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: 'rgba(255,255,255,0.04)',
    padding: 13,
    gap: 10,
  },
  orderBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  orderBadgeTxt: {color: '#fff', fontSize: 12, fontWeight: '700'},
  resetBtn: {alignSelf: 'flex-end', paddingVertical: 4},
  resetTxt: {fontSize: 12, fontWeight: '600'},
  contentTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#e8f1f8',
    marginBottom: 12,
  },
  contentBody: {
    fontSize: 15,
    color: 'rgba(232,241,248,0.75)',
    lineHeight: 24,
    marginBottom: 24,
  },
})
